import React, { useMemo, useEffect, useRef } from 'react';
import { Tabs, Tab, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT_TAB_CATEGORIES, getDocumentCategoryLabel } from '../../utils/documentCategories';

const DocumentationTabs = ({
    selectedCategory,
    onCategoryChange,
    categoryCounts = {},
    searchTerm = '',
    fromMachineCard = false,
}) => {
    const { t } = useTranslation();

    // FIXED: Track if we've set the initial category
    const hasSetInitialCategoryRef = useRef(false);

    const categories = useMemo(() => {
        const allCategories = DOCUMENT_TAB_CATEGORIES.map((value) => ({
            value,
            label: getDocumentCategoryLabel(t, value),
        }));

        // if (fromMachineCard) {
        //     return allCategories.filter(cat =>
        //         cat.value === 'manual'
        //     );
        // }

        return allCategories;
    }, [t, fromMachineCard]);

    // Filter out categories with zero count
    const visibleCategories = useMemo(() => {

        const isSearching = searchTerm.trim().length > 0;

        const hasAnyDocuments = categories.some(cat => {
            const counts = categoryCounts[cat.value];
            if (!counts) return false;

            const count = isSearching ? counts.matched : counts.total;
            return count > 0;
        });
        if (!hasAnyDocuments) {
            return categories;
        }
        return categories.filter(cat => {
            const counts = categoryCounts[cat.value];
            if (!counts) return false;

            const count = isSearching ? counts.matched : counts.total;
            return count > 0;
        });
    }, [categories, categoryCounts, searchTerm]);

    useEffect(() => {
    // Only run once on initial mount
    if (hasSetInitialCategoryRef.current) return;
    if (!selectedCategory) return;
    if (!categoryCounts || Object.keys(categoryCounts).length === 0) return;

    hasSetInitialCategoryRef.current = true;
}, []);


    // FIXED: Reset initialization flag when selected category becomes invisible due to filters
    useEffect(() => {
        if (hasSetInitialCategoryRef.current && selectedCategory) {
            const isSelectedVisible = visibleCategories.some(cat => cat.value === selectedCategory);
            if (!isSelectedVisible && visibleCategories.length > 0) {
                // Selected category is no longer visible, allow reinitialization
                
                hasSetInitialCategoryRef.current = false;
            }
        }
    }, [selectedCategory, visibleCategories]);

    // FIXED: Reset the flag when search term changes significantly
    useEffect(() => {
        // Only reset if user is actively searching (not just clearing search)
        if (searchTerm.trim().length > 0) {
            // Don't reset the flag - let user stay on their selected category while searching
        }
    }, [searchTerm]);

    const handleChange = (event, newValue) => {
        
        // Mark that we've set a category (user action)
        hasSetInitialCategoryRef.current = true;
        onCategoryChange(newValue);
    };

    // Get the correct count based on whether search is active
    const getCategoryCount = (categoryValue) => {
        const counts = categoryCounts[categoryValue];
        if (!counts) return 0;

        // If searching, show matched count; otherwise show total
        if (searchTerm.trim()) {
            return counts.matched || 0;
        }
        return counts.total || 0;
    };

    return (
        <Box>
            <Tabs
                value={selectedCategory}
                onChange={handleChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="documentation category tabs"
                className="profile-tabs"
                sx={{
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        minHeight: 48,
                        alignItems: 'flex-start',
                        paddingTop: '12px',
                        paddingBottom: '12px'
                    }
                }}
            >
                {visibleCategories.map((cat) => {
                    const count = getCategoryCount(cat.value);
                    const displayLabel = count > 0 ? `${cat.label} (${count})` : cat.label;

                    return (
                        <Tab
                            key={cat.value}
                            label={displayLabel}
                            value={cat.value}
                            sx={{
                                minWidth: 'auto',
                                paddingLeft: count > 0 ? '16px' : '24px',
                                paddingRight: '16px',

                                // Selected state 
                                '&.Mui-selected': {
                                    fontWeight: 700,
                                    color: 'primary.main',
                                }
                            }}
                        />
                    );
                })}
            </Tabs>
        </Box>
    );
};

export default DocumentationTabs;
